import React,{ useState, useEffect, useCallback } from "react"
import { Head } from "@inertiajs/react"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Switch } from "@/components/ui/switch"
import { Separator } from "@/components/ui/separator"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { User, Bell, Save, Mail, Plus, Edit, Trash2, TestTube, MoreVertical, Eye, EyeOff, Link, Upload, X } from "lucide-react"
import { useUsers, UserProvider } from "@/lib/contexts/user-context"
import { useToast } from "@/hooks/use-toast"
import { useForm, usePage, router as inertiaRouter } from '@inertiajs/react'
import { AuthenticatedLayout } from "@/components/layout/authenticated-layout"
import { UserSlackConnection } from "@/components/slack/user-slack-connection"
import Cropper from 'react-easy-crop'

export default function SettingsPage() {
  const { props } = usePage()
  
  return (
    <>
      <Head title="DME | Settings" />
      <AuthenticatedLayout>
        <UserProvider initialCurrentUser={props.user || null}>
          <SettingsContent />
        </UserProvider>
      </AuthenticatedLayout>
    </>
  )
}

function SettingsContent() {
  // Check URL parameters for tab selection
  const urlParams = new URLSearchParams(window.location.search);
  const tabFromUrl = urlParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || "account")
  const { currentUser: contextCurrentUser, updateUser, setCurrentUser } = useUsers()
  const { props } = usePage()
  // Use props.user if available (from Inertia), otherwise fall back to context
  const currentUser = props.user || contextCurrentUser
  const { toast } = useToast()
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    department: "",
  })
  const [avatarPreview, setAvatarPreview] = useState(currentUser?.avatar || null)
  const [avatarFile, setAvatarFile] = useState(null)
  const [showCropModal, setShowCropModal] = useState(false)
  const [imageSrc, setImageSrc] = useState(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
  const [avatarProcessing, setAvatarProcessing] = useState(false)
  const { post: postNotifications, processing: notificationsProcessing } = useForm({
    email_notifications: [],
    app_notifications: [],
  })

  // Email accounts state
  const [emailAccounts, setEmailAccounts] = useState([])
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false)
  const [selectedEmailAccount, setSelectedEmailAccount] = useState(null)
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false)
  const [isEmailTesting, setIsEmailTesting] = useState(null)
  const [isModalEmailTesting, setIsModalEmailTesting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState("")
  const [emailFormData, setEmailFormData] = useState({
    name: "",
    email: "",
    from_name: "",
    smtp_host: "",
    smtp_port: 587,
    smtp_encryption: "tls",
    smtp_username: "",
    smtp_password: "",
    is_default: false,
    is_active: true,
  })

  // Email provider presets
  const emailProviders = [
    {
      name: "Gmail",
      value: "gmail",
      smtp_host: "smtp.gmail.com",
      smtp_port: 587,
      smtp_encryption: "tls",
      instructions: "Gmail requires an App Password — your regular Gmail password will not work for SMTP"
    },
    {
      name: "Outlook / Hotmail",
      value: "outlook",
      smtp_host: "smtp-mail.outlook.com", 
      smtp_port: 587,
      smtp_encryption: "tls",
      instructions: "Use your Outlook/Hotmail address. If sign-in fails, you may need an App Password when multi-factor authentication is enabled."
    },
  ]

  const getEmailHealthBadge = (account) => {
    switch (account.health_status) {
      case "verified":
        return (
          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
            Verified
          </Badge>
        )
      case "failed":
        return (
          <Badge variant="destructive" className="bg-red-100 text-red-800 border-red-200">
            Failed
          </Badge>
        )
      case "never_tested":
        return (
          <Badge variant="outline" className="text-muted-foreground">
            Never tested
          </Badge>
        )
      default:
        return (
          <Badge variant="outline">
            Unknown
          </Badge>
        )
    }
  }

  useEffect(() => {
    if (currentUser) {
      setFormData({
        firstName: currentUser.firstName || "",
        lastName: currentUser.lastName || "",
        email: currentUser.email || "",
        department: currentUser?.department?.name || "No Department",
      })
    }
  }, [currentUser])

  // Load email accounts
  useEffect(() => {
    loadEmailAccounts()
  }, [])

  const loadEmailAccounts = async () => {
    try {
      const response = await fetch('/email-accounts')
      const data = await response.json()
      setEmailAccounts(data.accounts || [])
    } catch (error) {
      console.error('Failed to load email accounts:', error)
    }
  }

  const handleAvatarChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({ title: 'Please select an image file', variant: 'destructive' })
        return
      }
      
      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        toast({ title: 'Image size must be less than 2MB', variant: 'destructive' })
        return
      }

      // Read file and show crop modal
      const reader = new FileReader()
      reader.onloadend = () => {
        setImageSrc(reader.result)
        setShowCropModal(true)
      }
      reader.readAsDataURL(file)
    }
  }

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image()
      image.addEventListener('load', () => resolve(image))
      image.addEventListener('error', (error) => reject(error))
      image.src = url
    })

  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      throw new Error('No 2d context')
    }

    // Set canvas size to match the cropped area
    canvas.width = pixelCrop.width
    canvas.height = pixelCrop.height

    // Draw the cropped image
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    )

    // Convert canvas to blob
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'))
          return
        }
        resolve(blob)
      }, 'image/jpeg', 0.95)
    })
  }

  const handleCropComplete = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      return
    }

    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels)
      
      // Create a File object from the blob
      const file = new File([croppedImage], 'avatar.jpg', { type: 'image/jpeg' })
      setAvatarFile(file)
      
      // Update preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setAvatarPreview(reader.result)
      }
      reader.readAsDataURL(file)
      
      // Close crop modal
      setShowCropModal(false)
      setImageSrc(null)
    } catch (error) {
      console.error('Error cropping image:', error)
      toast({ title: 'Failed to crop image', variant: 'destructive' })
    }
  }

  const handleSaveAvatar = () => {
    if (!currentUser || !avatarFile) {
      toast({ title: 'Please select an image to upload', variant: 'destructive' })
      return
    }

    setAvatarProcessing(true)

    // Create FormData manually to ensure the file is included
    const formData = new FormData()
    formData.append('avatar', avatarFile)
    
    // Use Inertia router directly with FormData - this ensures the file is always included
    inertiaRouter.post('/settings/avatar', formData, {
      forceFormData: true,
      onSuccess: (page) => {
        toast({ title: 'Profile picture updated!' })
        // Clear the file selection
        setAvatarFile(null)
        setAvatarProcessing(false)
        // Update the user context with the new avatar immediately
        if (page?.props?.user) {
          setCurrentUser(page.props.user)
        }
        // The redirect from the backend will automatically update the page and user data
        // The header will update automatically via usePage() in user-nav
      },
      onError: (errors) => {
        setAvatarProcessing(false)
        toast({ 
          title: errors.avatar || errors.message || 'Failed to update profile picture', 
          variant: 'destructive' 
        })
      },
      onFinish: () => {
        setAvatarProcessing(false)
      },
    })
  }

  useEffect(() => {
    if (currentUser?.avatar) {
      setAvatarPreview(currentUser.avatar)
    }
  }, [currentUser])

  const [emailNotifications, setEmailNotifications] = useState([
    'email-tasks', 'email-workflows', 'email-comments', 'email-system',
  ])
  const [appNotifications, setAppNotifications] = useState([
    'app-tasks', 'app-workflows', 'app-comments', 'app-system',
  ])

  const handleToggleNotification = (type, id) => {
    if (type === 'email') {
      setEmailNotifications((prev) =>
        prev.includes(id) ? prev.filter((n) => n !== id) : [...prev, id]
      )
    } else {
      setAppNotifications((prev) =>
        prev.includes(id) ? prev.filter((n) => n !== id) : [...prev, id]
      )
    }
  }

  const handleSaveNotifications = () => {
    postNotifications('/settings/notifications', {
      data: {
        email_notifications: emailNotifications,
        app_notifications: appNotifications,
      },
      onSuccess: () => {
        toast({ title: 'Notification preferences updated!' })
      },
      onError: () => {
        toast({ title: 'Failed to update notification preferences', variant: 'destructive' })
      },
    })
  }

  // Email account functions
  const resetEmailForm = () => {
    setEmailFormData({
      name: "",
      email: "",
      from_name: "",
      smtp_host: "",
      smtp_port: 587,
      smtp_encryption: "tls",
      smtp_username: "",
      smtp_password: "",
      is_default: false,
      is_active: true,
    })
    setSelectedEmailAccount(null)
    setSelectedProvider("")
    setShowPassword(false)
  }

  const openEmailModal = (account = null) => {
    if (account) {
      setSelectedEmailAccount(account)
      setEmailFormData({
        name: account.name,
        email: account.email,
        from_name: account.from_name,
        smtp_host: account.smtp_host,
        smtp_port: account.smtp_port,
        smtp_encryption: account.smtp_encryption,
        smtp_username: account.smtp_username,
        smtp_password: "", // Don't populate password for security
        is_default: account.is_default,
        is_active: account.is_active,
      })
      // Try to detect provider based on SMTP host
      const provider = emailProviders.find(p => p.smtp_host === account.smtp_host)
      setSelectedProvider(provider ? provider.value : "")
    } else {
      resetEmailForm()
    }
    setIsEmailModalOpen(true)
  }

  const handleProviderChange = (providerValue) => {
    setSelectedProvider(providerValue)
    const provider = emailProviders.find(p => p.value === providerValue)
    
    if (provider) {
      setEmailFormData(prev => ({
        ...prev,
        smtp_host: provider.smtp_host,
        smtp_port: provider.smtp_port,
        smtp_encryption: provider.smtp_encryption,
        name: prev.name || provider.name + " Account",
      }))
    }
  }

  const closeEmailModal = () => {
    setIsEmailModalOpen(false)
    setTimeout(resetEmailForm, 300)
  }

  const handleEmailSubmit = async (e) => {
    e.preventDefault()

    if (!selectedEmailAccount && !selectedProvider) {
      toast({
        title: "Error",
        description: "Please select an email provider.",
        variant: "destructive",
      })
      return
    }

    setIsEmailSubmitting(true)

    try {
      const url = selectedEmailAccount 
        ? `/email-accounts/${selectedEmailAccount.id}`
        : '/email-accounts'
      
      const method = selectedEmailAccount ? 'PUT' : 'POST'
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify(emailFormData)
      })

      const data = await response.json()

      if (response.ok) {
        toast({
          title: "Success",
          description: data.message,
        })
        closeEmailModal()
        loadEmailAccounts()
      } else {
        const validationMessage = data.errors?.smtp_password?.[0]
          || data.message
          || data.error
          || 'Failed to save email account'
        throw new Error(validationMessage)
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to save email account",
        variant: "destructive",
      })
    } finally {
      setIsEmailSubmitting(false)
    }
  }

  const handleEmailDelete = async (account) => {
    if (!confirm(`Are you sure you want to delete "${account.name}"?`)) {
      return
    }

    try {
      const response = await fetch(`/email-accounts/${account.id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      })

      const data = await response.json()

      if (response.ok) {
        toast({
          title: "Success",
          description: data.message,
        })
        loadEmailAccounts()
      } else {
        throw new Error(data.error || 'Failed to delete email account')
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete email account",
        variant: "destructive",
      })
    }
  }

  const handleEmailTest = async (account) => {
    setIsEmailTesting(account.id)

    try {
      const response = await fetch(`/email-accounts/${account.id}/test`, {
        method: 'POST',
        headers: {
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      })

      const data = await response.json()

      toast({
        title: data.success ? "Connection Test Successful" : "Connection Test Failed",
        description: data.message,
        variant: data.success ? "default" : "destructive",
      })

      loadEmailAccounts()
    } catch (error) {
      toast({
        title: "Test Failed",
        description: "Failed to test email connection",
        variant: "destructive",
      })
    } finally {
      setIsEmailTesting(null)
    }
  }

  const handleModalEmailTest = async () => {
    if (!selectedProvider) {
      toast({
        title: "Error",
        description: "Please select an email provider first.",
        variant: "destructive",
      })
      return
    }

    if (!emailFormData.email || !emailFormData.smtp_host || !emailFormData.smtp_username) {
      toast({
        title: "Error",
        description: "Please fill in the email address and SMTP settings before testing.",
        variant: "destructive",
      })
      return
    }

    if (!selectedEmailAccount && !emailFormData.smtp_password) {
      toast({
        title: "Error",
        description: "Password is required to test a new email account.",
        variant: "destructive",
      })
      return
    }

    setIsModalEmailTesting(true)

    try {
      const response = await fetch('/email-accounts/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify({
          ...emailFormData,
          email_account_id: selectedEmailAccount?.id ?? null,
        })
      })

      const data = await response.json()

      toast({
        title: data.success ? "Connection Test Successful" : "Connection Test Failed",
        description: data.message || data.error,
        variant: data.success ? "default" : "destructive",
      })
    } catch (error) {
      toast({
        title: "Test Failed",
        description: error.message || "Failed to test email connection",
        variant: "destructive",
      })
    } finally {
      setIsModalEmailTesting(false)
    }
  }

  return (
    <div className="flex flex-col gap-6 pb-8 pt-6 px-4 md:px-6 w-full">
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Manage your account settings and preferences</p>
      </div>

      <Tabs defaultValue="account" value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex overflow-x-auto pb-2">
          <TabsList className="flex h-auto flex-wrap">
            <TabsTrigger value="account" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span>Account</span>
            </TabsTrigger>
            <TabsTrigger value="email-accounts" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <span>Email Accounts</span>
            </TabsTrigger>
            {/* <TabsTrigger value="integrations" className="flex items-center gap-2">
              <Link className="h-4 w-4" />
              <span>Integrations</span>
            </TabsTrigger> */}
          </TabsList>
        </div>

        <TabsContent value="account" className="space-y-4">
          <Card className="w-full">
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
              <CardDescription>Your account details and profile information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="first-name">First name</Label>
                  <Input 
                    id="first-name" 
                    value={formData.firstName}
                    readOnly
                    className="bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last-name">Last name</Label>
                  <Input 
                    id="last-name" 
                    value={formData.lastName}
                    readOnly
                    className="bg-muted"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email" 
                  type="email" 
                  value={formData.email}
                  readOnly
                  className="bg-muted"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Input 
                  id="department" 
                  value={formData.department}
                  readOnly
                  className="bg-muted"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="w-full">
            <CardHeader>
              <CardTitle>Profile Picture</CardTitle>
              <CardDescription>Upload a custom profile picture (Max 2MB, JPG, PNG, GIF, or WebP)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="h-24 w-24 rounded-full bg-muted flex items-center justify-center overflow-hidden border-2 border-primary">
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="Profile" className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                      <User className="h-12 w-12" />
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <label htmlFor="avatar-upload">
                      <Button size="sm" type="button" asChild>
                        <span className="cursor-pointer">
                          <Upload className="mr-2 h-4 w-4" />
                          Choose Image
                        </span>
                      </Button>
                    </label>
                    <input
                      id="avatar-upload"
                      type="file"
                      accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                      onChange={handleAvatarChange}
                      className="hidden"
                    />
                    {avatarFile && (
                      <Button size="sm" onClick={handleSaveAvatar} disabled={avatarProcessing}>
                        {avatarProcessing ? 'Uploading...' : 'Save'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Crop Modal */}
        <Dialog open={showCropModal} onOpenChange={setShowCropModal}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Crop your new profile picture</DialogTitle>
              <DialogDescription>
                Adjust the image to fit the circular profile icon
              </DialogDescription>
            </DialogHeader>
            <div className="relative w-full h-[400px] bg-black rounded-lg overflow-hidden">
              {imageSrc && (
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  cropShape="round"
                  showGrid={false}
                  style={{
                    containerStyle: {
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                    },
                  }}
                />
              )}
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Zoom</Label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowCropModal(false)
                setImageSrc(null)
                setAvatarFile(null)
              }}>
                Cancel
              </Button>
              <Button onClick={handleCropComplete}>
                Set new profile picture
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* <TabsContent value="notifications" className="space-y-4">
          <Card className="w-full">
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>Choose how you want to be notified</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Email Notifications</h3>
                <Separator />

                {[
                  { id: "email-tasks", label: "Task assignments and updates" },
                  { id: "email-workflows", label: "Workflow status changes" },
                  { id: "email-comments", label: "Comments and mentions" },
                  { id: "email-system", label: "System notifications" },
                ].map((item) => (
                  <div key={item.id} className="flex items-center justify-between">
                    <Label htmlFor={item.id} className="flex-1">
                      {item.label}
                    </Label>
                    <Switch id={item.id} checked={emailNotifications.includes(item.id)} onCheckedChange={() => handleToggleNotification('email', item.id)} />
                  </div>
                ))}

                <h3 className="mt-6 text-lg font-medium">In-App Notifications</h3>
                <Separator />

                {[
                  { id: "app-tasks", label: "Task assignments and updates" },
                  { id: "app-workflows", label: "Workflow status changes" },
                  { id: "app-comments", label: "Comments and mentions" },
                  { id: "app-system", label: "System notifications" },
                ].map((item) => (
                  <div key={item.id} className="flex items-center justify-between">
                    <Label htmlFor={item.id} className="flex-1">
                      {item.label}
                    </Label>
                    <Switch id={item.id} checked={appNotifications.includes(item.id)} onCheckedChange={() => handleToggleNotification('app', item.id)} />
                  </div>
                ))}
              </div>
            </CardContent>
            <CardFooter>
              <Button onClick={handleSaveNotifications} disabled={notificationsProcessing}>
                <Save className="mr-2 h-4 w-4" />
                Save Preferences
              </Button>
            </CardFooter>
          </Card>
        </TabsContent> */}

        <TabsContent value="email-accounts" className="space-y-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-2xl font-bold tracking-tight">Email Accounts</h2>
              <p className="text-muted-foreground">
                Configure SMTP email accounts for sending system emails
              </p>
            </div>
            <Button onClick={() => openEmailModal()} className="gap-2">
              <Plus className="h-4 w-4" />
              Add Account
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account Name</TableHead>
                      <TableHead>Email Address</TableHead>
                      <TableHead>SMTP Host</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Health</TableHead>
                      <TableHead>Default</TableHead>
                      <TableHead>Last Tested</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {emailAccounts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="h-24 text-center">
                          <div className="flex flex-col items-center gap-2">
                            <Mail className="h-8 w-8 text-muted-foreground" />
                            <p className="text-muted-foreground">
                              No email accounts configured.
                            </p>
                            <Button onClick={() => openEmailModal()} className="gap-2 mt-2">
                              <Plus className="h-4 w-4" />
                              Add Account
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      emailAccounts.map((account) => (
                        <TableRow 
                          key={account.id} 
                          className="cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => openEmailModal(account)}
                        >
                          <TableCell>
                            <div>
                              <div className="font-medium">{account.name}</div>
                              <div className="text-sm text-muted-foreground">
                                {account.from_name}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{account.email}</TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div>{account.smtp_host}:{account.smtp_port}</div>
                              <div className="text-muted-foreground capitalize">
                                {account.smtp_encryption}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={account.is_active ? "bg-green-100 text-green-800" : "text-muted-foreground"}>
                              {account.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              {getEmailHealthBadge(account)}
                              {account.health_status === "failed" && account.test_result && (
                                <p className="text-xs text-red-600 max-w-[220px] truncate" title={account.test_result}>
                                  {account.test_result.replace(/^Connection failed:\s*/i, "")}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {account.is_default && (
                              <Badge variant="outline">Default</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {account.last_tested_at || "Never"}
                            </div>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  className="h-8 w-8 p-0"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <span className="sr-only">Open menu</span>
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                <DropdownMenuItem 
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleEmailTest(account)
                                  }}
                                  disabled={isEmailTesting === account.id}
                                >
                                  <TestTube className="mr-2 h-4 w-4" />
                                  {isEmailTesting === account.id ? "Testing..." : "Test Connection"}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleEmailDelete(account)
                                  }}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete Account
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* <TabsContent value="integrations" className="space-y-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-2xl font-bold tracking-tight">Personal Integrations</h2>
              <p className="text-muted-foreground">
                Connect your personal accounts to enable individual integrations
              </p>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Slack Connection</CardTitle>
              <CardDescription>
                Connect your personal Slack account to send messages and access your channels
              </CardDescription>
            </CardHeader>
            <CardContent>
              <UserSlackConnection />
            </CardContent>
          </Card>
        </TabsContent> */}
      </Tabs>

      {/* Email Account Modal */}
      <Dialog open={isEmailModalOpen} onOpenChange={closeEmailModal}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedEmailAccount ? "Edit Email Account" : "Add Email Account"}
            </DialogTitle>
            <DialogDescription>
              Configure SMTP settings for sending emails from this account.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEmailSubmit}>
            <div className="grid gap-6 py-4">
              {/* Email Provider Selection */}
              <div className="space-y-3">
                <Label htmlFor="provider">Email Provider</Label>
                <Select value={selectedProvider} onValueChange={handleProviderChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose your email provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {emailProviders.map((provider) => (
                      <SelectItem key={provider.value} value={provider.value}>
                        {provider.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedProvider && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="text-sm text-blue-800 dark:text-blue-200">
                      <p className="font-medium mb-2">
                        <strong>Setup Instructions:</strong> {emailProviders.find(p => p.value === selectedProvider)?.instructions}
                      </p>
                      
                      {selectedProvider === "gmail" && (
                        <div className="space-y-2 mt-3">
                          <p className="font-medium">Quick Steps for Gmail:</p>
                          <ol className="list-decimal list-inside space-y-1 ml-2">
                            <li>Enable 2-Step Verification on your Google account</li>
                            <li>Go to <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">Google App Passwords</a></li>
                            <li>Create an app password for &quot;Mail&quot; (name it DME)</li>
                            <li>Paste the 16-character app password in the field below — not your regular Gmail password</li>
                          </ol>
                          <p className="text-xs mt-2 italic">Google blocks regular passwords for SMTP when 2FA is enabled.</p>
                        </div>
                      )}

                      {selectedProvider === "outlook" && (
                        <div className="space-y-2 mt-3">
                          <p className="font-medium">Quick Steps for Outlook / Hotmail:</p>
                          <ol className="list-decimal list-inside space-y-1 ml-2">
                            <li>Use your full Outlook/Hotmail email address as the username</li>
                            <li>Try your regular account password first</li>
                            <li>If authentication fails with MFA enabled, create an app password at <a href="https://account.microsoft.com/security" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">Microsoft Account Security</a></li>
                            <li>Ensure SMTP AUTH is enabled for your mailbox (some org accounts disable it)</li>
                          </ol>
                          <p className="text-xs mt-2 italic">Personal Outlook.com accounts usually work with a regular password; work/school accounts may require admin approval.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Basic Information */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="account-name">Account Name</Label>
                  <Input
                    id="account-name"
                    value={emailFormData.name}
                    onChange={(e) => setEmailFormData({ ...emailFormData, name: e.target.value })}
                    placeholder="e.g., Support Team, Marketing"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    A friendly name to identify this email account
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="from-name">Sender Name</Label>
                  <Input
                    id="from-name"
                    value={emailFormData.from_name}
                    onChange={(e) => setEmailFormData({ ...emailFormData, from_name: e.target.value })}
                    placeholder="e.g., DME Support, Your Company"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    The name recipients will see as the sender
                  </p>
                </div>
              </div>

              {/* Email Credentials */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email-address">Email Address</Label>
                  <Input
                    id="email-address"
                    type="email"
                    value={emailFormData.email}
                    onChange={(e) => {
                      setEmailFormData({
                        ...emailFormData,
                        email: e.target.value,
                        smtp_username: e.target.value,
                      })
                    }}
                    placeholder="your.email@gmail.com"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email-password">Email Password</Label>
                  <div className="relative">
                    <Input
                      id="email-password"
                      type={showPassword ? "text" : "password"}
                      value={emailFormData.smtp_password}
                      onChange={(e) => setEmailFormData({ ...emailFormData, smtp_password: e.target.value })}
                      placeholder={selectedEmailAccount ? "Leave blank to keep current password" : "Your email password"}
                      required={!selectedEmailAccount}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {selectedProvider === "gmail" && "Use an App Password instead of your regular Gmail password"}
                    {selectedProvider === "outlook" && "Use your regular Outlook/Hotmail password"}
                    {!selectedProvider && "Select a provider above, then use your email account password"}
                  </p>
                </div>
              </div>

              {/* Account Settings */}
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is-active"
                    checked={emailFormData.is_active}
                    onCheckedChange={(checked) => setEmailFormData({ ...emailFormData, is_active: checked })}
                  />
                  <Label htmlFor="is-active">Active</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is-default"
                    checked={emailFormData.is_default}
                    onCheckedChange={(checked) => setEmailFormData({ ...emailFormData, is_default: checked })}
                  />
                  <Label htmlFor="is-default">Set as Default</Label>
                  <p className="text-xs text-muted-foreground ml-2">
                    Use this account for all system emails
                  </p>
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={closeEmailModal}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleModalEmailTest}
                disabled={isModalEmailTesting || isEmailSubmitting || !selectedProvider}
              >
                {isModalEmailTesting ? "Testing..." : "Test Connection"}
              </Button>
              <Button type="submit" disabled={isEmailSubmitting || isModalEmailTesting}>
                {isEmailSubmitting ? "Saving..." : (selectedEmailAccount ? "Update Account" : "Add Account")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
